import { useEffect, useMemo, useState } from "react";

import Api from "lib/api/httpRequestHelper";
import { resultToFamiliesAndRules } from "../utils/family-builder";

const defaultBusinessRules = {
  global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
  vitrines: {},
  categories: {},
};

/**
 * Busca a vitrine (PDP) e deriva famílias/regras de negócio a partir do resultado da API.
 * Refaz a busca quando `idPessoaLoja` muda (usuário com mais de uma loja selecionou uma no
 * carrinho), para que o idVitrineVirtualB2bCliente de cada item já venha correto para a loja escolhida.
 */
export default function useVitrineData({ showAlert, idPessoaLoja }) {
  const [loading, setLoading] = useState(true);
  const [vitrineApiResult, setVitrineApiResult] = useState(null);

  useEffect(() => {
    async function fetchVitrines() {
      setLoading(true);
      try {
        const query = idPessoaLoja ? `?idPessoaLoja=${idPessoaLoja}` : "";
        const response = await Api.get(
          "virtual-showcase-b2b-list-pdp",
          "showcaseB2B",
          query,
          true,
        );

        if (!response) {
          setVitrineApiResult(null);
          return;
        }

        if (response.Success === false) {
          setVitrineApiResult(null);
          const msg =
            response.ErrorMessage ||
            response.Message ||
            response.ValidationMessage ||
            "Não foi possível carregar as vitrines.";
          showAlert("error", "Atenção", msg);
          return;
        }

        const vitrines = response.Result?.Vitrines;
        const produtos = response.Result?.Produtos;
        if (vitrines && vitrines.length > 0) {
          const nomesLinhas =
            response.Result?.NomesLinhasComerciais ??
            response.Result?.nomesLinhasComerciais ??
            [];
          setVitrineApiResult({
            Vitrines: vitrines,
            Produtos: Array.isArray(produtos) ? produtos : [],
            NomesLinhasComerciais: Array.isArray(nomesLinhas)
              ? nomesLinhas
              : [],
          });
        } else {
          setVitrineApiResult(null);
          showAlert(
            "error",
            "Atenção",
            "Você não possui vitrines disponíveis.",
          );
        }
      } catch {
        setVitrineApiResult(null);
      } finally {
        setLoading(false);
      }
    }

    fetchVitrines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPessoaLoja]);

  const derived = useMemo(() => {
    if (!vitrineApiResult) return null;
    return resultToFamiliesAndRules(vitrineApiResult);
  }, [vitrineApiResult]);

  const effectiveFamilies = derived?.families ?? {};
  const effectiveBusinessRules = derived?.businessRules ?? defaultBusinessRules;
  const effectiveBrandKeys = derived?.brandKeys ?? [];
  const effectiveBrandLabels = derived?.brandLabels ?? {};
  const vitrinesMeta = derived?.vitrinesMeta ?? {};

  const primaryVitrine = useMemo(() => {
    const vitrines = vitrineApiResult?.Vitrines;
    if (!Array.isArray(vitrines) || vitrines.length === 0) return null;
    const comCliente = vitrines.find(
      (v) => v.IdVitrineVirtualB2bCliente != null,
    );
    return comCliente ?? vitrines[0];
  }, [vitrineApiResult]);

  const brandKeysWithProducts = useMemo(() => {
    const set = new Set();
    Object.entries(effectiveFamilies).forEach(([brandKey, familiesObj]) => {
      const total = Object.values(familiesObj || {}).reduce(
        (acc, fam) => acc + (fam.products?.length || 0),
        0,
      );
      if (total > 0) set.add(brandKey);
    });
    return set;
  }, [effectiveFamilies]);

  const availableBrandKeys = useMemo(
    () => [...brandKeysWithProducts],
    [brandKeysWithProducts],
  );

  return {
    loading,
    vitrineApiResult,
    effectiveFamilies,
    effectiveBusinessRules,
    effectiveBrandKeys,
    effectiveBrandLabels,
    vitrinesMeta,
    primaryVitrine,
    brandKeysWithProducts,
    availableBrandKeys,
  };
}
